import { useLocalStorage } from "@/lib/hooks/useLocalStorage";
import { Info } from "@phosphor-icons/react";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

type TutorialBoxProps = {
  storageKey: string;
  title?: string;
  message: string;
  onStartTour?: () => void;
};

// Custom hook for managing tutorial state that can be shared with SideTitle
export function useTutorialState(storageKey: string) {
  const [dismissed, setDismissed] = useLocalStorage<boolean>(
    `${storageKey}-dismissed`,
    false
  );
  const [closing, setClosing] = useState(false);

  const reopen = useCallback(() => {
    setClosing(false);
    setDismissed(false);
  }, [setDismissed]);

  const dismiss = useCallback(() => {
    setClosing(true);
    setTimeout(() => setDismissed(true), 150);
  }, [setDismissed]);

  return {
    dismissed,
    closing,
    reopen,
    dismiss,
  };
}

type TutorialBoxWithStateProps = TutorialBoxProps & {
  tutorialState: ReturnType<typeof useTutorialState>;
};

export default function TutorialBox({
  storageKey: _storageKey,
  title = "Quick guide",
  message,
  onStartTour,
  tutorialState,
}: TutorialBoxWithStateProps) {
  const { t } = useTranslation(["common"]);
  const { dismissed, closing, dismiss } = tutorialState;

  if (dismissed) {
    return null;
  }

  return (
    <div
      role="region"
      aria-label="Tool tutorial"
      className={`mt-4 rounded-md border border-border bg-background p-4 shadow-sm transition-opacity ${closing ? "opacity-0" : "opacity-100"}`}
    >
      <div className="flex items-start gap-3">
        <Info className="mt-0.5 text-foreground" size={20} />
        <div className="flex-1">
          <div className="mb-1 text-sm font-semibold text-foreground">
            {title}
          </div>
          <p className="text-sm text-muted-foreground">{message}</p>
          <div className="flex gap-2 mt-3">
            {onStartTour && (
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
                onClick={onStartTour}
              >
                {t("tutorial.start")}
              </button>
            )}
            <button
              type="button"
              className="inline-flex items-center rounded-md border border-input bg-white px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
              onClick={dismiss}
            >
              {t("tutorial.dismiss")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
